import { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  authApi,
  type OrganizationMember,
  type PlatformOrganization,
  type PlatformUser,
} from '@/api/auth'
import { useAuth } from '@/auth/AuthContext'

const { Title } = Typography

export default function OrganizationPage() {
  const { identity, refresh } = useAuth()
  const [members, setMembers] = useState<OrganizationMember[]>([])
  const [organizations, setOrganizations] = useState<PlatformOrganization[]>([])
  const [users, setUsers] = useState<PlatformUser[]>([])
  const isAdmin = ['owner', 'admin'].includes(identity?.organization.role || '')

  const loadMembers = async () => {
    try {
      setMembers((await authApi.members()).items)
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载成员失败')
    }
  }

  useEffect(() => {
    void loadMembers()
    if (identity?.user.is_platform_admin) {
      void Promise.all([
        authApi.platformOrganizations().then((data) => setOrganizations(data.items)),
        authApi.platformUsers().then((data) => setUsers(data.items)),
      ])
    }
  }, [identity?.user.is_platform_admin])

  const invite = async (values: { email: string; role: 'admin' | 'member' | 'viewer' }) => {
    try {
      await authApi.invite(values)
      message.success('邀请邮件已发送')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '邀请失败')
    }
  }

  const memberPanel = (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="企业信息">
        <Form
          layout="inline"
          initialValues={{ name: identity?.organization.name }}
          onFinish={async ({ name }) => {
            try {
              await authApi.updateOrganization(name)
              await refresh()
              message.success('企业名称已更新')
            } catch (error) {
              message.error(error instanceof Error ? error.message : '更新失败')
            }
          }}
        >
          <Form.Item name="name" rules={[{ required: true }]}>
            <Input disabled={!isAdmin} style={{ width: 280 }} />
          </Form.Item>
          {isAdmin && <Button type="primary" htmlType="submit">保存</Button>}
        </Form>
      </Card>

      {isAdmin && (
        <Card title="邀请成员">
          <Form layout="inline" onFinish={invite} initialValues={{ role: 'member' }}>
            <Form.Item name="email" rules={[{ required: true }, { type: 'email' }]}>
              <Input placeholder="成员邮箱" style={{ width: 260 }} />
            </Form.Item>
            <Form.Item name="role">
              <Select
                style={{ width: 130 }}
                options={[
                  { value: 'admin', label: '管理员' },
                  { value: 'member', label: '成员' },
                  { value: 'viewer', label: '只读' },
                ]}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit">生成邀请</Button>
          </Form>
        </Card>
      )}

      <Card title="企业成员">
        <Table
          rowKey="user_id"
          pagination={false}
          dataSource={members}
          columns={[
            { title: '成员', render: (_, row) => row.display_name || row.email },
            { title: '邮箱', dataIndex: 'email' },
            {
              title: '角色',
              render: (_, row) =>
                isAdmin && row.role !== 'owner' ? (
                  <Select
                    value={row.role}
                    style={{ width: 110 }}
                    options={[
                      { value: 'admin', label: '管理员' },
                      { value: 'member', label: '成员' },
                      { value: 'viewer', label: '只读' },
                    ]}
                    onChange={async (role) => {
                      await authApi.updateMember(row.user_id, { role })
                      await loadMembers()
                    }}
                  />
                ) : <Tag>{row.role}</Tag>,
            },
            { title: '状态', dataIndex: 'status', render: (value) => <Tag>{value}</Tag> },
          ]}
        />
      </Card>
    </Space>
  )

  const platformPanel = (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="企业列表">
        <Table
          rowKey="id"
          dataSource={organizations}
          columns={[
            { title: '企业', dataIndex: 'name' },
            { title: '标识', dataIndex: 'slug' },
            { title: '成员数', dataIndex: 'member_count' },
            { title: '状态', dataIndex: 'status' },
          ]}
        />
      </Card>
      <Card title="用户列表">
        <Table
          rowKey="id"
          dataSource={users}
          columns={[
            { title: '用户', dataIndex: 'display_name' },
            { title: '邮箱', dataIndex: 'email' },
            { title: '状态', dataIndex: 'status' },
            {
              title: '平台管理员',
              dataIndex: 'is_platform_admin',
              render: (value) => value ? <Tag color="blue">是</Tag> : '否',
            },
          ]}
        />
      </Card>
    </Space>
  )

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: 24 }}>
      <Title level={3}>企业与成员</Title>
      {identity?.user.is_platform_admin ? (
        <Tabs items={[
          { key: 'organization', label: '当前企业', children: memberPanel },
          { key: 'platform', label: '平台管理', children: platformPanel },
        ]} />
      ) : memberPanel}
    </div>
  )
}
